import { PlusIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';
import Box from '@mui/joy/Box';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SetupEvaluation } from '@/Pages/Onboarding/components/SetupEvaluation';
import Breadcrumbs from '@/components/Breadcrumbs';
import EmptyState from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { Pane } from '@/components/Pane';
import {
  DATASETS_PAGE_DESCRIPTION,
  DATASETS_PAGE_TITLE,
} from '@/constants/pageTitlesAndDescriptionConstants';
import { Button } from '@/design-system/components/Button';
import { usePermissions } from '@/hooks/usePermissions.tsx';
import { useStoredResourceTags } from '@/hooks/useStoredResourceTags/useStoredResourceTags';
import { useOrganizationId } from '@/hooks/useSwr';

import { DatasetCrudPaneWithButton } from '../../components/DatasetCrudPane/DatasetCrudPaneWithButton';
import { DatasetsTable } from '../../components/DatasetsTable';
import { PageTitle } from '../../components/PageTitle';
import { appDatasetsPath, appOrganizationPath } from '../../utils/constants';

export function NoDatasets({ hasTags }: { hasTags: boolean }) {
  return (
    <div className="py-10">
      <EmptyState
        title={
          hasTags
            ? 'There are no datasets with current set of resource tags. Try removing tags to see all datasets.'
            : 'There are no datasets.'
        }
      />
    </div>
  );
}

const Datasets = () => {
  const organizationId = useOrganizationId();
  const navigate = useNavigate();
  const [isNewEvalOpen, setIsNewEvalOpen] = useState(false);
  const { authorize, isLoading: isPermsLoading } = usePermissions();
  const { selectedTags } = useStoredResourceTags();

  const newTestRunBtn = (
    <Button
      size="md"
      color="primary"
      onClick={() => setIsNewEvalOpen(true)}
      leftDecorator={PlusIcon}
      variant="normal"
    >
      New Experiment
    </Button>
  );

  if (isPermsLoading) {
    return <LinearProgress />;
  }
  if (!authorize('datasets:read')) {
    return <ErrorBanner className="m-4">Forbidden</ErrorBanner>;
  }

  return (
    <div className="px-4 pb-6 pt-3">
      <Breadcrumbs />
      <Box display="flex" alignItems={'center'} gap={2}>
        <PageTitle description={DATASETS_PAGE_DESCRIPTION}>
          {DATASETS_PAGE_TITLE}
        </PageTitle>
        {authorize('datasets:create') && (
          <div className="flex gap-2">
            {newTestRunBtn}

            <DatasetCrudPaneWithButton
              onSuccess={(id) =>
                navigate(
                  `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${id}`
                )
              }
            />
          </div>
        )}
      </Box>

      <DatasetsTable
        emptyState={<NoDatasets hasTags={selectedTags.length > 0} />}
      />

      <Pane
        title="Setup Evaluation"
        open={isNewEvalOpen}
        onClose={() => setIsNewEvalOpen(false)}
      >
        <SetupEvaluation />
      </Pane>
    </div>
  );
};

export default Datasets;
